import type { TenantWithOwner } from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminTenants, usePartnersList } from "@/lib/hooks/use-admin";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useT } from "@/lib/i18n/use-t";
import { TenantDialog } from "./tenant-dialog";
import { T } from "./tenant-wrapper.i18n";
import { TenantsTable } from "./tenants-table";

const PAGE_SIZE = 20;

export function TenantWrapper() {
  const t = useT(T);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantWithOwner | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [page, setPage] = useState(1);

  // QL-136 — filter, search and paging all run on the server.
  const { tenants, pagination, isLoading, isFetching, mutate } = useAdminTenants({
    filter,
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  });
  const { partners } = usePartnersList();

  // A new search or filter starts from the first page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filter]);

  // A page can empty out after a delete on the last page; step back to the
  // last page that still exists so the pager stays reachable.
  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  // "No tenants yet" is wrong once a search or filter has removed them all:
  // the tenants exist, none of them match.
  const emptyMessage = debouncedSearch.trim()
    ? t.noSearchResults
    : filter !== "all"
      ? t.noMatchingTenants
      : undefined;

  const handleCreate = () => {
    setEditingTenant(null);
    setDialogOpen(true);
  };

  const handleEdit = (tenant: TenantWithOwner) => {
    setEditingTenant(tenant);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingTenant(null);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-4 flex-1">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t.filterTenants} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t.allTenants}</SelectItem>
              <SelectItem value="partners">{t.partnersOnly}</SelectItem>
              {partners.length > 0 && <SelectSeparator />}
              {partners.map((partner) => (
                <SelectItem key={partner.id} value={partner.id}>
                  {t.ownedBy} {partner.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SearchInput
            className="flex-1 max-w-sm"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
        </div>

        <Button onClick={handleCreate}>{t.addTenant}</Button>
      </div>

      <TenantsTable
        tenants={tenants}
        onEdit={handleEdit}
        mutate={mutate}
        emptyMessage={emptyMessage}
        page={pagination.page}
        pageSize={pagination.pageSize}
        totalCount={pagination.totalCount}
        totalPages={pagination.totalPages}
        onPageChange={setPage}
        isLoading={isLoading}
        isFetching={isFetching}
      />

      <TenantDialog
        open={dialogOpen}
        tenant={editingTenant}
        onClose={handleDialogClose}
        mutate={mutate}
        isQuantfolio={true}
        partners={partners}
      />
    </>
  );
}
